// Lo que la pestaña «Historial crediticio» pinta en cada celda. Un campo AUSENTE («el silo todavía
// no lo publica») nunca se confunde con `null` («lo sé y no aplica») ni con `0` (una afirmación).
import { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import type { LoanBorrowerCreditItem, LoanSaleContextReason } from '@/api/services/loan-credit';
import { SALE_CONTEXT_REASON_NOTE } from '../../saleContext';
import type { Gap } from './customer360Gaps';

/** Las etiquetas del mock. «Pendiente» a secas: «de activación» no entra en el ancho de la celda. */
export const HISTORY_STATUS_LABEL: Record<LoanCreditStatusEnum, string> = {
    [LoanCreditStatusEnum.PENDING_ACTIVATION]: 'Pendiente',
    [LoanCreditStatusEnum.ACTIVE]: 'Activo',
    [LoanCreditStatusEnum.IN_ARREARS]: 'En mora',
    [LoanCreditStatusEnum.SETTLED]: 'Liquidado',
    [LoanCreditStatusEnum.CANCELLED]: 'Cancelado',
    [LoanCreditStatusEnum.DISCARDED]: 'Descartado',
    [LoanCreditStatusEnum.WRITTEN_OFF]: 'Baja contable',
};

/**
 * La regla de color del mock: el rojo es SOLO la mora, el verde solo el vigente y el azul solo el
 * liquidado. Todo lo demás es gris — un crédito cancelado no pide nada de nadie.
 */
export const HISTORY_STATUS_CHIP_CLASS: Record<LoanCreditStatusEnum, string> = {
    [LoanCreditStatusEnum.PENDING_ACTIVATION]: 'border-transparent bg-muted text-muted-foreground',
    [LoanCreditStatusEnum.ACTIVE]: 'border-transparent bg-success-soft text-success',
    [LoanCreditStatusEnum.IN_ARREARS]: 'border-transparent bg-danger-soft text-danger',
    [LoanCreditStatusEnum.SETTLED]: 'border-transparent bg-info-soft text-info',
    [LoanCreditStatusEnum.CANCELLED]: 'border-transparent bg-muted text-muted-foreground',
    [LoanCreditStatusEnum.DISCARDED]: 'border-transparent bg-muted text-muted-foreground',
    [LoanCreditStatusEnum.WRITTEN_OFF]: 'border-transparent bg-muted text-muted-foreground',
};

/**
 * Vigente en esta pestaña es activo o en mora, y nada más. `isOpenCredit` cuenta además el
 * pendiente de activación, que todavía no debe un peso: sumaría un saldo que no existe.
 */
export function isCurrentCredit(credit: LoanBorrowerCreditItem): boolean {
    return (
        credit.status === LoanCreditStatusEnum.ACTIVE ||
        credit.status === LoanCreditStatusEnum.IN_ARREARS
    );
}

/* ================================================================================
   HUECOS — uno por campo. Dos celdas con el mismo motivo no dicen cuál de las dos falta.
   ================================================================================ */

/**
 * Un hueco de esta pestaña. `read: true` es un dato que SE LEYÓ y vino vacío; `read: false` es uno
 * que nadie llegó a leer — decirlos igual acusa al silo de no registrar lo que nunca se le pidió.
 */
export interface CreditHistoryGap extends Gap {
    readonly read: boolean;
}

/** El endpoint viejo todavía en Dev no manda el saldo por crédito. Un cero lo inventaría. */
export const BALANCE_UNPUBLISHED: CreditHistoryGap = {
    read: false,
    label: 'Sin publicar',
    note: 'El silo de crédito todavía no publica el saldo insoluto de cada crédito en este endpoint. No sabemos cuánto debe: pintar un cero afirmaría que ya no debe nada.',
};

/** `null` es una afirmación del silo, no un hueco de lectura: ese crédito nunca contrajo deuda. */
export const BALANCE_NOT_APPLICABLE: CreditHistoryGap = {
    read: true,
    label: 'No aplica',
    note: 'Este crédito nunca llegó a contraer la deuda —se canceló o se descartó antes de activarse—, así que no tiene saldo insoluto que mostrar.',
};

/** La deuda total del crédito no viene en la respuesta: el silo todavía no la publica. */
export const DEBT_UNPUBLISHED: CreditHistoryGap = {
    read: false,
    label: 'Sin publicar',
    note: 'El silo de crédito todavía no publica la deuda total de este crédito. No sabemos cuánto debe: pintar un cero afirmaría que ya no debe nada.',
};

/** Igual que el saldo: `null` es un crédito que nunca contrajo la deuda. */
export const DEBT_NOT_APPLICABLE: CreditHistoryGap = {
    read: true,
    label: 'No aplica',
    note: 'Este crédito nunca llegó a contraer la deuda —se canceló o se descartó antes de activarse—, así que no tiene deuda que mostrar.',
};

export const PROGRESS_UNPUBLISHED: CreditHistoryGap = {
    read: false,
    label: 'Sin publicar',
    note: 'El silo de crédito todavía no publica cuántos cupones lleva cubiertos cada crédito en este endpoint. El plan de pagos completo se ve en Cartera.',
};

export const ARREARS_UNPUBLISHED: CreditHistoryGap = {
    read: false,
    label: 'Sin publicar',
    note: 'El silo de crédito todavía no publica los días de atraso de cada crédito en este endpoint. No sabemos si está al día: pintar un guion diría que no debe nada vencido.',
};

export const ARREARS_AMOUNT_UNPUBLISHED: CreditHistoryGap = {
    read: false,
    label: 'monto sin publicar',
    note: 'El silo dice cuántos días lleva vencido este crédito pero todavía no publica cuánto suman las cuotas vencidas impagas.',
};

/** Lo que el resumen NO puede afirmar cuando algún vigente no trae su saldo. */
export const SUMMARY_BALANCE_UNPUBLISHED: CreditHistoryGap = {
    read: false,
    label: 'sin publicar',
    note: 'Al menos uno de los créditos vigentes no trae su saldo insoluto, así que la suma no se puede afirmar. Sumar solo los que sí lo traen daría un total más bajo que el real.',
};

/* ================================================================================
   PRODUCTO — sale de la venta del wizard, que es OTRA lectura y falla por su cuenta.
   ================================================================================ */

export const PRODUCT_SALE_UNPUBLISHED: CreditHistoryGap = {
    read: false,
    label: 'Sin publicar',
    note: 'El silo de crédito todavía no publica la venta del wizard detrás de cada crédito en este endpoint, y el crédito por sí solo no guarda marca ni modelo del equipo.',
};

export const PRODUCT_NO_SALE: CreditHistoryGap = {
    read: true,
    label: 'Sin venta',
    note: 'Se buscó la venta que originó este crédito y no existe: nadie registró qué equipo se financió.',
};

export const PRODUCT_SALE_UNREADABLE: CreditHistoryGap = {
    read: false,
    label: 'No se pudo leer',
    note: 'No se pudo consultar la venta que originó este crédito, así que no sabemos qué equipo se financió. No es que la venta no exista: es que la lectura falló.',
};

export const PRODUCT_SALE_INVALID_REFERENCE: CreditHistoryGap = {
    read: false,
    label: 'Referencia inválida',
    note: 'La referencia con la que este crédito apunta a su venta está mal formada, así que nunca se va a poder resolver el equipo financiado. Reintentar no lo cambia.',
};

export const PRODUCT_NOT_RECORDED: CreditHistoryGap = {
    read: true,
    label: 'Sin modelo',
    note: 'La venta que originó este crédito se leyó completa y no registró marca ni modelo del equipo.',
};

/** El motivo del hueco con el detalle del silo pegado atrás, cuando lo manda. */
function withReason(
    gap: CreditHistoryGap,
    reason: LoanSaleContextReason | undefined,
): CreditHistoryGap {
    if (reason === undefined) return gap;
    return { ...gap, note: `${gap.note} ${SALE_CONTEXT_REASON_NOTE[reason]}` };
}

/** Marca y modelo del equipo financiado, o por qué no se saben. Nunca el SKU crudo. */
export function creditProductOf(credit: LoanBorrowerCreditItem): string | CreditHistoryGap {
    const sale = credit.saleContext;
    if (sale === undefined) return PRODUCT_SALE_UNPUBLISHED;
    if (sale.status === 'NOT_FOUND') return withReason(PRODUCT_NO_SALE, sale.reason);
    if (sale.status === 'UNAVAILABLE') return withReason(PRODUCT_SALE_UNREADABLE, sale.reason);
    if (sale.status === 'INVALID_ID')
        return withReason(PRODUCT_SALE_INVALID_REFERENCE, sale.reason);

    const name = [sale.productBrand, sale.productModel]
        .filter((part): part is string => part !== null && part.trim() !== '')
        .join(' ');
    return name === '' ? PRODUCT_NOT_RECORDED : name;
}

/* ================================================================================
   CELDAS DE CIFRAS — ausente, `null` y `0` son tres cosas distintas y se dicen distinto.
   ================================================================================ */

/** El saldo insoluto del crédito, o el motivo por el que no hay uno que pintar. */
export function creditBalanceOf(credit: LoanBorrowerCreditItem): number | CreditHistoryGap {
    if (credit.outstandingBalanceCents === undefined) return BALANCE_UNPUBLISHED;
    if (credit.outstandingBalanceCents === null) return BALANCE_NOT_APPLICABLE;
    return credit.outstandingBalanceCents;
}

/** La deuda total del crédito (capital, interés e IVA que faltan), o por qué no hay una. */
export function creditDebtOf(credit: LoanBorrowerCreditItem): number | CreditHistoryGap {
    if (credit.totalDebtCents === undefined) return DEBT_UNPUBLISHED;
    if (credit.totalDebtCents === null) return DEBT_NOT_APPLICABLE;
    return credit.totalDebtCents;
}

/** Con algún crédito que traiga la deuda total, la columna «Saldo» pasa a ser «Deuda total». */
export function hasTotalDebt(credits: readonly LoanBorrowerCreditItem[]): boolean {
    return credits.some((credit) => credit.totalDebtCents !== undefined);
}

/** `pagados/totales`: los cupones cubiertos sobre el plazo del crédito. */
export function creditProgressOf(credit: LoanBorrowerCreditItem): string | CreditHistoryGap {
    if (credit.paidInstallments === undefined) return PROGRESS_UNPUBLISHED;
    return `${String(credit.paidInstallments)}/${String(credit.termWeeks)}`;
}

/** Qué pinta la columna Mora: el atraso, el guion del que no debe, o el hueco del que no se sabe. */
export type CreditArrears =
    | { kind: 'in-arrears'; days: number; amount: number | CreditHistoryGap }
    /** El guion GRIS del mock. Lleva su motivo: al día y sin cuotas fechadas no son lo mismo. */
    | { kind: 'none'; note: string }
    | { kind: 'unpublished'; gap: CreditHistoryGap };

const NO_ARREARS_NOTE = 'Este crédito no tiene ninguna cuota vencida impaga: está al día.';

const NO_DUE_DATES_NOTE =
    'Este crédito no tiene cuotas fechadas contra las cuales medir un atraso, así que no hay mora que afirmar ni que descartar.';

export function creditArrearsOf(credit: LoanBorrowerCreditItem): CreditArrears {
    if (credit.overdueDays === undefined) return { kind: 'unpublished', gap: ARREARS_UNPUBLISHED };
    if (credit.overdueDays === null) return { kind: 'none', note: NO_DUE_DATES_NOTE };
    if (credit.overdueDays === 0) return { kind: 'none', note: NO_ARREARS_NOTE };

    const amount =
        typeof credit.overdueAmountCents === 'number'
            ? credit.overdueAmountCents
            : ARREARS_AMOUNT_UNPUBLISHED;
    return { kind: 'in-arrears', days: credit.overdueDays, amount };
}

/* ================================================================================
   RESUMEN — los tres campos de la primera tarjeta.
   ================================================================================ */

export interface CreditHistorySummary {
    total: number;
    currentCount: number;
    /** La suma de los saldos vigentes, o el motivo por el que no se puede afirmar. */
    currentBalanceCents: number | CreditHistoryGap;
    /** La suma de las deudas totales vigentes; `null` si alguno no la trae. */
    currentDebtCents: number | null;
    settledCount: number;
}

export function creditHistorySummaryOf(credits: LoanBorrowerCreditItem[]): CreditHistorySummary {
    const current = credits.filter(isCurrentCredit);
    const settledCount = credits.filter(
        (credit) => credit.status === LoanCreditStatusEnum.SETTLED,
    ).length;
    // Un solo vigente sin saldo publicado tumba la suma entera: parcial se leería como total.
    const unpublished = current.some((credit) => credit.outstandingBalanceCents === undefined);

    return {
        total: credits.length,
        currentCount: current.length,
        currentBalanceCents: unpublished
            ? SUMMARY_BALANCE_UNPUBLISHED
            : current.reduce((sum, credit) => sum + (credit.outstandingBalanceCents ?? 0), 0),
        // Igual que el saldo: un vigente sin deuda publicada no deja afirmar la suma.
        currentDebtCents:
            hasTotalDebt(credits) &&
            current.every((credit) => typeof credit.totalDebtCents === 'number')
                ? current.reduce((sum, credit) => sum + (credit.totalDebtCents ?? 0), 0)
                : null,
        settledCount,
    };
}
